import React, { useState, useEffect } from 'react';
import '../../scss/match/motmVote.scss';
import PlayerShirt from './PlayerShirt';
import api from '../../services/apiService';

const MotmVote = ({ match_id, players: matchPlayers, isAdmin, isAcademy }) => {
    const [players, setPlayers] = useState([]);
    const [votedPlayer, setVotedPlayer] = useState(null);
    const [showResults, setShowResults] = useState(false);
    const [adminView, setAdminView] = useState('votes');
    const [isLoading, setIsLoading] = useState(true);
    const [isVotingClosed, setIsVotingClosed] = useState(false);
    const voter_id = sessionStorage.getItem('player_id');
    
    // Determine when to show vote counts:
    // - For non-admins: show only if voting is closed OR they've voted
    // - For admins: show if in results view OR voting is closed
    const showVoteCounts = isAdmin 
        ? (adminView === 'votes' || isVotingClosed)
        : (isVotingClosed || votedPlayer !== null);

    const loadPlayers = async () => {
        try {
            setIsLoading(true);
            // Check if voting is closed
            const matchResponse = await api.get(`${process.env.REACT_APP_API_URL}/get_match_result.php?match_id=${match_id}`);
            const matchData = matchResponse.data;
            console.log('Match data for voting status:', matchData);
            // Check for MOTM data - either motm field (real API) or motm_first_name/motm_last_name (dummy data)
            const votingClosed = !!(matchData.data?.motm || (matchData.data?.motm_first_name && matchData.data?.motm_last_name));
            console.log('Voting closed status:', votingClosed, 'motm:', matchData.data?.motm, 'motm_first_name:', matchData.data?.motm_first_name, 'motm_last_name:', matchData.data?.motm_last_name);
            setIsVotingClosed(votingClosed);
            
            // Get vote counts
            const votesResponse = await api.get(`${process.env.REACT_APP_API_URL}/vote_motm.php?match_id=${match_id}`);
            const votesData = votesResponse.data;
            // Merge player data with vote counts
            const playersWithVotes = matchPlayers.map(player => ({
                ...player,
                vote_count: votesData.find(v => v.player_id === player.player_id)?.vote_count || 0,
            }));
            
            setPlayers(playersWithVotes);
            
            // Convert voter_id to string for comparison since player_id is stored as string
            const currentPlayer = matchPlayers.find(player => player.player_id === voter_id.toString());
            
            // Check if current user has already voted
            if (currentPlayer && currentPlayer.motm_vote !== null) {
                console.log('User has already voted for:', currentPlayer.motm_vote);
                setVotedPlayer(parseInt(currentPlayer.motm_vote));
                setShowResults(true);
            }
        } catch (error) {
            console.error('Error in loadPlayers:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (matchPlayers?.length > 0) {
            loadPlayers();
        } 
    }, [match_id, matchPlayers]);

    const handleVote = async (player_id) => {
        console.log('Attempting to vote for player:', player_id);
        console.log('Current voter:', voter_id);
        
        // Check if current user has already voted
        const currentPlayer = players.find(p => p.player_id === voter_id.toString());
        console.log('Current player data:', currentPlayer);
        
        if (currentPlayer?.motm_vote) {
            console.log('Vote blocked - user already voted for:', currentPlayer.motm_vote);
            return;
        }

        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/vote_motm.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    match_id: match_id,
                    player_id: player_id,
                    voter_id: voter_id
                })
            });
            
            if (response.data.success) {
                // Update state
                setVotedPlayer(player_id);
                setShowResults(true);
                
                // Refresh the results
                await loadPlayers();
            }
        } catch (error) {
            console.error('Error submitting vote:', error);
        }
    };

    const handleCloseVoting = async () => {
        // Check if any votes have been cast
        if (highestVotes === 0) {
            alert('Cannot close voting - no votes have been cast yet.');
            return;
        }

        const shouldClose = window.confirm('Are you sure you want to close the voting and announce the winner(s)? This cannot be undone.');
        
        if (!shouldClose) {
            return;
        }

        try {
            const response = await api.post(`${process.env.REACT_APP_API_URL}/vote_motm.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'close',
                    match_id
                })
            });

            const result = response.data;
            
            if (result.success) {
                alert(result.message);
                loadPlayers(); // Refresh the display
            } else {
                alert('Failed to close voting: ' + result.error);
            }
        } catch (error) {
            console.error('Error closing voting:', error);
            alert('An error occurred while closing the voting');
        }
    };

    // Sort all players by votes and name
    const sortedPlayers = players.sort((a, b) => 
        b.vote_count - a.vote_count || 
        a.last_name.localeCompare(b.last_name)
    );

    // Get the highest vote count
    const highestVotes = Math.max(...players.map(p => p.vote_count || 0), 0);

    // Get players with the highest votes
    const topPlayers = players.filter(p => p.vote_count === highestVotes);

    if (isLoading) {
        return <div className="motm-vote loading">Loading...</div>;
    }

    if (!players || players.length === 0) {
        return (
                    <div className="motm-vote">
            <h2>{isAcademy ? "Parents' Player of the Match" : 'Player of the Match'}</h2>
                <div className="no-players">No players found for this match</div>
            </div>
        );
    }

    return (
        <div className="motm-vote">
            <h3 className='tab-title'>{isAcademy ? "Parents' Player of the Match" : 'Player of the Match'}</h3>
            <p className='intro'>
                {isAcademy
                    ? (isVotingClosed || showVoteCounts ? 'Results' : votedPlayer ? 'Thanks for voting' : 'Vote for the player of the match')
                    : (isVotingClosed ? 'Player of the Match Results' : 
                votedPlayer ? 'Thank you for voting! Current Results:' :
                !showVoteCounts ? 'Choose your Player of the Match' : 
                'Player of the Match Voting Results')}
            </p>
            {isAdmin && !isVotingClosed ? (
                <div className="button-container">
                    <button 
                        className={`action-button ${adminView === 'voting' ? 'active' : ''}`}
                        onClick={() => setAdminView('voting')}
                    >
                        Voting
                    </button>
                    <button 
                        className={`action-button ${adminView === 'votes' ? 'active' : ''}`}
                        onClick={() => setAdminView('votes')}
                    >
                        Results
                    </button>
                </div>
            ) : null}

            <div className="players-list">
                {sortedPlayers.map((player, index) => (
                    <div key={player.player_id} className="player-row">
                        <div className="player-info">
                            <div className="player-details">
                                <span className="player-name">
                                    {player.shirt_number}. {player.first_name} {player.last_name}
                                </span>
                                <span className="player-motm-stats">
                                    {player.position} • {player.goals || 0} goals
                                </span>
                            </div>
                            {showVoteCounts && (
                                <div className="vote-display">
                                    {player.vote_count > 0 && player.vote_count === highestVotes && (
                                        <span className="leader-trophy">🏆</span>
                                    )}
                                    <span className="vote-count">
                                        {player.vote_count}
                                    </span>
                                </div>
                            )}
                        </div>
                        {!isVotingClosed && !votedPlayer && (!isAdmin || adminView === 'voting') && (
                            <button 
                                className="vote-button"
                                onClick={() => handleVote(player.player_id)}
                                disabled={
                                    votedPlayer === player.player_id || 
                                    player.player_id === voter_id
                                }
                                title={player.player_id === voter_id ? "You cannot vote for yourself" : ""}
                            >
                                {player.player_id === voter_id ? "Can't Vote" : "Vote"}
                            </button>
                        )}
                    </div>
                ))}
            </div>
            {isAdmin && !isVotingClosed && (
                <div className="close-voting-section">
                    <p className="close-voting-text">
                        Ready to close the voting and announce the winner(s)? 
                        This will lock in the votes and notify all players of the result.
                        {topPlayers.length > 1 && (
                            <span className="tie-warning">
                                Note: There is currently a tie for first place. All tied players will be awarded MOTM.
                            </span>
                        )}
                    </p>
                    <div className='button-container'>
                        <button 
                            className="action-button active"
                            onClick={handleCloseVoting}
                        >
                            Close Voting
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MotmVote; 